import { ComponentRef, Injectable, Injector } from '@angular/core';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { LoadingComponent } from "../components/loading/loading.component";
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { DataLoadService } from "./data-load.service";

export class LoadingOverlayRef {
  constructor(private overlayRef: OverlayRef) {
  }

  close(): void {
    this.overlayRef.dispose();
  }
}

@Injectable()
export class LoadingService {
  private readonly bootstrap$ = new BehaviorSubject<boolean>(true);

  public readonly loading$: Observable<boolean>;

  constructor(private injector: Injector,
              private overlay: Overlay,
              private dataLoadService: DataLoadService) {
    this.loading$ = combineLatest([
      this.bootstrap$,
      this.dataLoadService.isInitializing$
    ]).pipe(
      map(([bootstrap, initializing]) => bootstrap || initializing),
      distinctUntilChanged()
    );
  }

  bootstrapComplete(): void {
    this.bootstrap$.next(false);
  }

  bootstrapReset(): void {
    this.bootstrap$.next(true);
  }

  open(): LoadingOverlayRef {
    const overlayRef = this.createOverlay();
    const dialogRef = new LoadingOverlayRef(overlayRef);
    this.attachDialogContainer(overlayRef, dialogRef);

    return dialogRef;
  }

  private createOverlay(): OverlayRef {
    const positionStrategy = this.overlay
      .position()
      .global()
      .centerHorizontally()
      .centerVertically();
    const overlayConfig = new OverlayConfig({
      hasBackdrop: true,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy
    });

    return this.overlay.create(overlayConfig);
  }

  private attachDialogContainer(overlayRef: OverlayRef, dialogRef: LoadingOverlayRef): LoadingComponent {
    const containerPortal = new ComponentPortal(LoadingComponent,);
    const containerRef: ComponentRef<LoadingComponent> = overlayRef.attach(containerPortal);

    return containerRef.instance;
  }
}
